import { JsonToGoSampleData } from '../../../../../DummyData/ConvertSampleJson';
import React, { useState } from 'react';

// Define types for our component
interface JsonToGoProps {}

// Go output format options
type GoOutputFormat = 'struct' | 'map' | 'interface';

// Go naming style options
type NamingStyle = 'pascal' | 'camel' | 'snake';

const JsonToGo: React.FC<JsonToGoProps> = () => {
  // State for the JSON input
  const [jsonInput, setJsonInput] = useState<string>('');
  // State for the Go output
  const [goOutput, setGoOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the package name
  const [packageName, setPackageName] = useState<string>('main');
  // State for the struct name
  const [structName, setStructName] = useState<string>('Response');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State for output format
  const [outputFormat, setOutputFormat] = useState<GoOutputFormat>('struct');
  // State for naming style
  const [namingStyle, setNamingStyle] = useState<NamingStyle>('pascal');
  // State to track if the conversion happened
  const [isGoGenerated, setIsGoGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for inline type definitions
  const [inlineTypes, setInlineTypes] = useState<boolean>(false);
  // State for adding json tags
  const [addJsonTags, setAddJsonTags] = useState<boolean>(true);
  // State for adding comments
  const [addComments, setAddComments] = useState<boolean>(true);

  // Handle JSON input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonInput(e.target.value);
    setError('');
    setIsGoGenerated(false);
  };

  // Handle package name change
  const handlePackageNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPackageName(e.target.value);
  };

  // Handle struct name change
  const handleStructNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStructName(e.target.value);
  };

  // Use example data
  const useExampleData = () => {

    
    setJsonInput(JSON.stringify(JsonToGoSampleData, null, 2));
    setError('');
    setIsGoGenerated(false);
    setGoOutput('');
  };

  // Beautify/Format JSON
  const beautifyJson = () => {
    try {
      if (!jsonInput.trim()) {
        setError('Please enter JSON data to format');
        return;
      }
      
      const parsedJson = JSON.parse(jsonInput);
      const beautified = JSON.stringify(parsedJson, null, 2);
      setJsonInput(beautified);
      setError('');
    } catch (e) {
      setError(`Invalid JSON format: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Validate JSON
  const validateJson = (jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setJsonInput('');
    setGoOutput('');
    setError('');
    setPackageName('main');
    setStructName('Response');
    setOutputFormat('struct');
    setNamingStyle('pascal');
    setIsGoGenerated(false);
    setInlineTypes(false);
    setAddJsonTags(true);
    setAddComments(true);
  };

  // Convert a string to PascalCase
  const toPascalCase = (str: string): string => {
    return str
      .replace(/[-_\s]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ''))
      .replace(/^(.)/, (c) => c.toUpperCase());
  };

  // Convert a string to camelCase
  const toCamelCase = (str: string): string => {
    const pascal = toPascalCase(str);
    return pascal.charAt(0).toLowerCase() + pascal.slice(1);
  };

  // Convert a string to snake_case
  const toSnakeCase = (str: string): string => {
    return str
      .replace(/([a-z])([A-Z])/g, '$1_$2')
      .replace(/[\s-]+/g, '_')
      .toLowerCase();
  };

  // Format field name according to the selected naming style
  const formatFieldName = (name: string): string => {
    switch (namingStyle) {
      case 'pascal':
        return toPascalCase(name);
      case 'camel':
        return toCamelCase(name);
      case 'snake':
        return toSnakeCase(name);
      default:
        return toPascalCase(name);
    }
  };

  // Determine Go type from a JavaScript value
  const determineGoType = (value: any, fieldName: string, parentName: string): string => {
    if (value === null) {
      return 'interface{}';
    }
    
    if (typeof value === 'string') {
      // Check if it looks like a date/time string
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/.test(value)) {
        return 'time.Time';
      }
      return 'string';
    }
    
    if (typeof value === 'number') {
      // Check if it's an integer
      if (Number.isInteger(value)) {
        if (value >= -2147483648 && value <= 2147483647) {
          return 'int';
        }
        return 'int64';
      }
      return 'float64';
    }
    
    if (typeof value === 'boolean') {
      return 'bool';
    }
    
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return '[]interface{}';
      }
      
      // Check if all elements are of the same type
      const sampleType = determineGoType(value[0], fieldName, parentName);
      const allSameType = value.every(item => 
        determineGoType(item, fieldName, parentName) === sampleType
      );
      
      if (allSameType) {
        return `[]${sampleType}`;
      }
      
      return '[]interface{}';
    }
    
    if (typeof value === 'object') {
      // If inline types are enabled, we'll return the type name to be created
      if (!inlineTypes) {
        const typeName = `${parentName}${formatFieldName(fieldName)}`;
        return typeName;
      }
      
      // Otherwise, we'll create an inline struct
      return generateInlineStruct(value, `${parentName}${formatFieldName(fieldName)}`);
    }
    
    return 'interface{}';
  };

  // Generate an inline struct definition
  const generateInlineStruct = (obj: any, parentTypeName: string): string => {
    let result = 'struct {\n';
    
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const fieldName = formatFieldName(key);
        const fieldType = determineGoType(obj[key], key, parentTypeName);
        const jsonTag = addJsonTags ? ` \`json:"${key}"\`` : '';
        
        result += `\t\t${fieldName} ${fieldType}${jsonTag}\n`;
      }
    }
    
    result += '\t}';
    return result;
  };

  // Generate struct or other Go type definitions
  const generateGoTypes = (obj: any, typeName: string, level = 0): { code: string, subTypes: string } => {
    let code = '';
    let subTypes = '';
    
    if (outputFormat === 'struct') {
      code += `type ${typeName} struct {\n`;
      
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const fieldName = formatFieldName(key);
          const fieldValue = obj[key];
          const fieldType = determineGoType(fieldValue, key, typeName);
          
          // Add comment if enabled
          if (addComments) {
            const valueType = Array.isArray(fieldValue) ? 'array' : typeof fieldValue;
            code += `\t// ${fieldName} ${valueType} value\n`;
          }
          
          // Add field definition with json tag
          const jsonTag = addJsonTags ? ` \`json:"${key}"\`` : '';
          code += `\t${fieldName} ${fieldType}${jsonTag}\n`;
          
          // Generate sub-types for nested objects if not using inline types
          if (typeof fieldValue === 'object' && fieldValue !== null && !Array.isArray(fieldValue) && !inlineTypes) {
            const subTypeName = `${typeName}${formatFieldName(key)}`;
            const { code: subTypeCode, subTypes: nestedSubTypes } = generateGoTypes(fieldValue, subTypeName, level + 1);
            subTypes += subTypeCode + '\n' + nestedSubTypes;
          }
        }
      }
      
      code += '}\n';
    } else if (outputFormat === 'interface') {
      code += `type ${typeName} interface {\n`;
      
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const fieldName = formatFieldName(key);
          const fieldValue = obj[key];
          const fieldType = determineGoType(fieldValue, key, typeName);
          
          // Add getter method
          const getterName = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
          code += `\tGet${getterName}() ${fieldType}\n`;
          
          // Generate sub-types for nested objects if not using inline types
          if (typeof fieldValue === 'object' && fieldValue !== null && !Array.isArray(fieldValue) && !inlineTypes) {
            const subTypeName = `${typeName}${formatFieldName(key)}`;
            const { code: subTypeCode, subTypes: nestedSubTypes } = generateGoTypes(fieldValue, subTypeName, level + 1);
            subTypes += subTypeCode + '\n' + nestedSubTypes;
          }
        }
      }
      
      code += '}\n';
    } else if (outputFormat === 'map') {
      code += `type ${typeName} map[string]interface{}\n`;
    }
    
    return { code, subTypes };
  };

  // Convert JSON to Go
  const convertJsonToGo = () => {
    setError('');
    setIsLoading(true);
    setIsGoGenerated(false);
    setGoOutput('');
    
    if (!jsonInput.trim()) {
      setError('Please enter JSON data');
      setIsLoading(false);
      return;
    }

    if (!validateJson(jsonInput)) {
      setError('Invalid JSON format. Please check your input.');
      setIsLoading(false);
      return;
    }

    try {
      const jsonData = JSON.parse(jsonInput);
      
      let goCode = `package ${packageName}\n\n`;
      
      // Add time import if needed (check if any fields are time.Time)
      const checkForTimeType = (obj: any): boolean => {
        if (typeof obj !== 'object' || obj === null) {
          return false;
        }
        
        if (Array.isArray(obj)) {
          return obj.some(item => checkForTimeType(item));
        }
        
        for (const key in obj) {
          if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const value = obj[key];
            
            if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/.test(value)) {
              return true;
            }
            
            if (typeof value === 'object' && value !== null && checkForTimeType(value)) {
              return true;
            }
          }
        }
        
        return false;
      };
      
      if (checkForTimeType(jsonData)) {
        goCode += 'import "time"\n\n';
      }
      
      // Generate the main type and any sub-types
      const { code, subTypes } = generateGoTypes(jsonData, structName);
      
      // Add all the type definitions
      goCode += subTypes;
      goCode += code;
      
      setGoOutput(goCode);
      setIsGoGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error generating Go code: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Copy Go code to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(goOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download Go file
  const downloadGo = () => {
    const blob = new Blob([goOutput], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${structName.toLowerCase()}.go`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">JSON to Go Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert JSON data to Go structs, interfaces, or maps. Paste your JSON data below to generate Go code with the appropriate types.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="jsonInput" className="font-medium text-gray-700">
                JSON Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={beautifyJson}
                >
                  Format JSON
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setJsonInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="jsonInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={jsonInput}
              onChange={handleInputChange}
              placeholder='Paste your JSON data here, e.g., {"name": "John", "age": 30}'
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="packageName" className="block text-sm font-medium text-gray-700 mb-1">
                  Package Name
                </label>
                <input
                  type="text"
                  id="packageName"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  value={packageName}
                  onChange={handlePackageNameChange}
                  placeholder="e.g., main"
                />
              </div>
              <div>
                <label htmlFor="structName" className="block text-sm font-medium text-gray-700 mb-1">
                  Root Type Name
                </label>
                <input
                  type="text"
                  id="structName"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  value={structName}
                  onChange={handleStructNameChange}
                  placeholder="e.g., Response"
                />
              </div>
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div>
                  <label htmlFor="outputFormat" className="block text-sm font-medium text-gray-700 mb-1">
                    Output Format
                  </label>
                  <select
                    id="outputFormat"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={outputFormat}
                    onChange={(e) => setOutputFormat(e.target.value as GoOutputFormat)}
                  >
                    <option value="struct">Struct</option>
                    <option value="interface">Interface</option>
                    <option value="map">Map</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="namingStyle" className="block text-sm font-medium text-gray-700 mb-1">
                    Field Naming Style
                  </label>
                  <select
                    id="namingStyle"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={namingStyle}
                    onChange={(e) => setNamingStyle(e.target.value as NamingStyle)}
                  >
                    <option value="pascal">PascalCase</option>
                    <option value="camel">camelCase</option>
                    <option value="snake">snake_case</option>
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="inlineTypes"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={inlineTypes}
                    onChange={(e) => setInlineTypes(e.target.checked)}
                  />
                  <label htmlFor="inlineTypes" className="ml-2 block text-sm text-gray-700">
                    Use Inline Type Definitions
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="addJsonTags"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={addJsonTags}
                    onChange={(e) => setAddJsonTags(e.target.checked)}
                  />
                  <label htmlFor="addJsonTags" className="ml-2 block text-sm text-gray-700">
                    Add JSON Tags
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="addComments"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={addComments}
                    onChange={(e) => setAddComments(e.target.checked)}
                  />
                  <label htmlFor="addComments" className="ml-2 block text-sm text-gray-700">
                    Add Comments
                  </label>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertJsonToGo}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to Go'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              Go Output
            </label>
            
            {isGoGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy Go Code'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadGo}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isGoGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                  <p>
                    Go code will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isGoGenerated && (
              <div className="flex-grow flex flex-col">
                <textarea
                  className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                  value={goOutput}
                  readOnly
                />
              </div>
            )}
          </div>
          
          {isGoGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">Go Code Successfully Generated!</p>
              </div>
              <p className="text-sm">
                The generated Go code includes properly typed structs with JSON tags for easy serialization/deserialization.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About JSON to Go Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool analyzes your JSON data structure and generates Go code that matches it. It infers types from your data values
              and creates appropriate Go structs, interfaces, or maps based on your preferences.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Type Inference</strong> - Automatically determines the appropriate Go types based on JSON values</li>
              <li><strong>Multiple Output Formats</strong> - Generate structs, interfaces, or maps</li>
              <li><strong>Naming Style Options</strong> - Choose between PascalCase, camelCase, or snake_case for field names</li>
              <li><strong>JSON Tags</strong> - Optional tags for compatibility with encoding/json package</li>
              <li><strong>Nested Structures</strong> - Properly handles nested objects and arrays</li>
              <li><strong>Type Comments</strong> - Optional comments for better code documentation</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use the "Format JSON" button to properly format and validate your JSON data before conversion</li>
              <li>Include representative sample data to ensure the correct types are inferred</li>
              <li>For time-related fields, use ISO 8601 format (e.g., "2023-01-01T00:00:00Z") to ensure proper time.Time type detection</li>
              <li>For large, complex JSON structures, consider setting "Use Inline Type Definitions" to false for more readable Go code</li>
              <li>Pascal case (e.g., UserProfile) is the conventional naming style for Go exported fields</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Common Use Cases</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>API Integration</strong> - Quickly create Go structs from API response samples</li>
              <li><strong>Data Modeling</strong> - Convert JSON data models to Go types for your projects</li>
              <li><strong>Code Generation</strong> - Generate boilerplate code for handling JSON in Go applications</li>
              <li><strong>Learning Go</strong> - See how JSON data maps to Go types as a learning exercise</li>
              <li><strong>Prototyping</strong> - Rapidly prototype applications that need to work with JSON data</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JsonToGo;